import FileUploader from "@via/components/file-uploader/file-uploader";
import Modal from "@via/components/modal.tsx";
import { type FC, useEffect } from "react";
import SelectComponent from "@via/components/select/select.tsx";
import { requiredValidation } from "@via/utils/form-validators.tsx";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  useRunReportDirectories,
  useUploadReport,
} from "@via/pages/projects/runs/runs.api.tsx";

interface UploadReportModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  runUUID: string;
}

const UploadReportModal: FC<UploadReportModalProps> = ({
  open,
  setOpen,
  runUUID,
}) => {
  const { runId = "" } = useParams();

  const {
    data: runReportDirectories,
    isLoading: isRunReportDirectoriesLoading,
  } = useRunReportDirectories(runId);

  const {
    mutate: uploadReport,
    isPending: isUploadReportLoading,
    isSuccess: isUploadReportSuccess,
  } = useUploadReport(runId, runUUID);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: "onChange",
  });

  const reportDirectory = watch("reportDirectory");

  const handleCreateRun = (payload): void => {
    uploadReport(payload);
  };

  useEffect(() => {
    if (isUploadReportSuccess) {
      setOpen(false);
    }
  }, [isUploadReportSuccess]);

  const onSubmit = handleSubmit(handleCreateRun);

  const handleFileUpload = (files: File[]): void => {
    const uploadedFile = files[0];
    setValue("file", uploadedFile);
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Upload Report"
      showButtons={false}>
      {/* The dialog has no confirm button. FileUploader's Upload button submits this form,
          and the success effect above closes the dialog. */}
      <form
        onSubmit={e => {
          e.preventDefault();
          onSubmit(e);
        }}>
        <div className="my-5">
          <div>
            <label className="block text-sm font-medium leading-6 text-gray-900">
              Choose Report Directory&nbsp;
              <span className="text-red-400">*</span>
            </label>
            <div className="mt-2" data-testid="runReportDirectorySelector">
              <SelectComponent
                control={control}
                enableClear={false}
                name="reportDirectory"
                rules={requiredValidation("Report Directory")}
                options={
                  runReportDirectories?.map(directory => ({
                    label: directory,
                    value: directory,
                  })) ?? []
                }
                isLoading={isRunReportDirectoriesLoading}
                placeholder="Choose Report Directory"
              />
              {errors.reportDirectory && (
                <span className="text-xs text-rose-500">
                  {typeof errors.reportDirectory.message === "string"
                    ? errors.reportDirectory.message
                    : null}
                </span>
              )}
            </div>
          </div>
          <div className="mt-4 flex align-items">
            <FileUploader
              onUpload={handleFileUpload}
              maxFiles={1}
              isLoading={isUploadReportLoading}
              progressPercentageList={[]}
              isDisabled={!reportDirectory}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default UploadReportModal;
